import axios from 'axios';
import { getAuthToken } from './authService';
import { getApiUrl } from '../utils/config';

// Interface cho thống kê đội
export interface TeamStatistics {
  teamId: number;
  teamName: string;
  imageUrl: string;
  tournamentId: number;
  tournamentName: string;
  totalDistance: number;
  totalMovingTime: number;
  totalElapsedTime: number;
  activityCount: number;
  userCount: number;
  topUser: UserStatistics | null;
  memberStats: UserStatistics[] | null;
}

// Interface cho thống kê người dùng
export interface UserStatistics {
  userId: number;
  fullName: string;
  avatarUrl: string;
  department: string | null;
  totalDistance: number;
  totalMovingTime: number | null;
  totalElapsedTime: number | null;
  activityCount: number;
  averagePace: number | null;
  averageSpeed: number | null;
  totalElevationGain: number | null;
  lastActivityDate: number[] | null;
}

// Interface cho thành tích
export interface Achievement {
  activityId: number;
  activityName: string;
  activityType: string;
  value: number;
  valueFormatted: string;
  activityDate: number[];
}

// Interface cho thành tích cá nhân tốt nhất
export interface PersonalBest {
  userId: number;
  fullName: string;
  avatarUrl: string;
  longestDistance: Achievement;
  longestMovingTime: Achievement;
  fastestSpeed: Achievement;
  bestPace: Achievement;
  highestElevationGain: Achievement;
}

// Interface cho thống kê giải đấu
export interface TournamentStatistics {
  tournamentId: number;
  tournamentName: string;
  description: string;
  startDate: number[];
  endDate: number[];
  totalDistance: number;
  totalMovingTime: number | null;
  totalElapsedTime: number | null;
  activityCount: number;
  teamCount: number;
  userCount: number;
  teamStats: any | null;
  topTeam: TeamStatistics | null;
  topUser: UserStatistics | null;
}

// Interface cho response API
export interface ApiResponse<T> {
  result: string;
  message: string;
  data: T;
}

/**
 * Lấy thống kê của đội
 * @param teamId ID của đội
 * @param startDateLocal Ngày bắt đầu (YYYY-MM-DDT00:00:00)
 * @param endDate Ngày kết thúc (YYYY-MM-DDT23:59:59)
 * @returns Thống kê của đội
 */
export const getTeamStatistics = async (
  teamId: number,
  startDateLocal: string = '2023-01-01T00:00:00',
  endDate: string = new Date().toISOString().split('T')[0] + 'T23:59:59'
): Promise<TeamStatistics> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get<ApiResponse<TeamStatistics>>(
      getApiUrl(`/api/stats/teams/${teamId}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy thống kê đội:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy thống kê đội');
  }
};

/**
 * Lấy thống kê của giải đấu
 * @param tournamentId ID của giải đấu
 * @param startDateLocal Ngày bắt đầu (YYYY-MM-DDT00:00:00)
 * @param endDate Ngày kết thúc (YYYY-MM-DDT23:59:59)
 * @returns Thống kê của giải đấu
 */
export const getTournamentStatistics = async (
  tournamentId: number,
  startDateLocal: string = '2023-01-01T00:00:00',
  endDate: string = new Date().toISOString().split('T')[0] + 'T23:59:59'
): Promise<TournamentStatistics> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get<ApiResponse<TournamentStatistics>>(
      getApiUrl(`/api/stats/tournaments/${tournamentId}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy thống kê giải đấu:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy thống kê giải đấu');
  }
};

/**
 * Lấy bảng xếp hạng người dùng trong đội
 * @param teamId ID của đội
 * @param startDateLocal Ngày bắt đầu (YYYY-MM-DDT00:00:00)
 * @param endDate Ngày kết thúc (YYYY-MM-DDT23:59:59)
 * @returns Danh sách xếp hạng người dùng trong đội
 */
export const getTeamUserRanking = async (
  teamId: number,
  startDateLocal: string = '2023-01-01T00:00:00',
  endDate: string = new Date().toISOString().split('T')[0] + 'T23:59:59'
): Promise<UserStatistics[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get<ApiResponse<UserStatistics[]>>(
      getApiUrl(`/api/stats/teams/${teamId}/user-ranking`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy bảng xếp hạng người dùng trong đội:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy bảng xếp hạng người dùng trong đội');
  }
};

/**
 * Lấy bảng xếp hạng người dùng trong giải đấu
 * @param tournamentId ID của giải đấu
 * @param startDateLocal Ngày bắt đầu (YYYY-MM-DDT00:00:00)
 * @param endDate Ngày kết thúc (YYYY-MM-DDT23:59:59)
 * @returns Danh sách xếp hạng người dùng trong giải đấu
 */
export const getTournamentUserRanking = async (
  tournamentId: number,
  startDateLocal: string = '2023-01-01T00:00:00',
  endDate: string = new Date().toISOString().split('T')[0] + 'T23:59:59'
): Promise<UserStatistics[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get<ApiResponse<UserStatistics[]>>(
      getApiUrl(`/api/stats/tournaments/${tournamentId}/user-ranking`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy bảng xếp hạng người dùng trong giải đấu:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy bảng xếp hạng người dùng trong giải đấu');
  }
};

/**
 * Lấy bảng xếp hạng đội trong giải đấu
 * @param tournamentId ID của giải đấu
 * @param startDateLocal Ngày bắt đầu (YYYY-MM-DDT00:00:00)
 * @param endDate Ngày kết thúc (YYYY-MM-DDT23:59:59)
 * @returns Danh sách xếp hạng đội trong giải đấu
 */
export const getTournamentTeamRanking = async (
  tournamentId: number,
  startDateLocal: string = '2023-01-01T00:00:00',
  endDate: string = new Date().toISOString().split('T')[0] + 'T23:59:59'
): Promise<TeamStatistics[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get<ApiResponse<TeamStatistics[]>>(
      getApiUrl(`/api/stats/tournaments/${tournamentId}/team-ranking`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          startDateLocal,
          endDate
        }
      }
    );

    return response.data.data;
  } catch (error: any) {
    console.error('Lỗi khi lấy bảng xếp hạng đội trong giải đấu:', error);
    throw new Error(error.response?.data?.message || 'Không thể lấy bảng xếp hạng đội trong giải đấu');
  }
};

/**
 * Lấy thành tích cá nhân tốt nhất của người dùng
 * @param userId ID của người dùng
 * @param startDateLocal Ngày bắt đầu (YYYY-MM-DDT00:00:00)
 * @param endDate Ngày kết thúc (YYYY-MM-DDT23:59:59)
 * @returns Thành tích cá nhân tốt nhất
 */
export const getPersonalBest = async (
  userId: number,
  startDateLocal: string = '2023-01-01T00:00:00',
  endDate: string = new Date().toISOString().split('T')[0] + 'T23:59:59'
): Promise<PersonalBest> => {
  try {
    console.log("Token:", getAuthToken());
    const apiUrl = getApiUrl(`/api/stats/users/${userId}/personal-best?startDateLocal=${startDateLocal}&endDate=${endDate}`);
    console.log("Gọi API:", apiUrl);

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`
      }
    });

    console.log("Kết quả API:", response.status, response.statusText);
    console.log("Headers:", response.headers);
    console.log("Dữ liệu trả về:", response.data);

    if (response.data && response.data.result === "SUCCESS" && response.data.data) {
      // Kiểm tra và đảm bảo không có null trong dữ liệu
      const data = response.data.data;
      
      // Đảm bảo các trường thành tích tồn tại
      if (!data.longestDistance) {
        data.longestDistance = null;
      }
      if (!data.longestMovingTime) {
        data.longestMovingTime = null;
      }
      if (!data.fastestSpeed) {
        data.fastestSpeed = null;
      }
      if (!data.bestPace) {
        data.bestPace = null;
      }
      if (!data.highestElevationGain) {
        data.highestElevationGain = null;
      }

      return data;
    } else {
      console.error("Dữ liệu API không hợp lệ:", response.data);
      throw new Error(response.data?.message || "Không thể lấy dữ liệu thành tích cá nhân");
    }
  } catch (error: any) {
    console.error("Lỗi API getPersonalBest:", error);
    if (error.response) {
      console.error("Chi tiết lỗi:", error.response.status, error.response.data);
    }
    throw new Error(error.message || "Có lỗi xảy ra khi lấy thành tích cá nhân");
  }
};

/**
 * Lấy danh sách tất cả người dùng
 * @returns Danh sách thông tin người dùng
 */
export const getAllUsers = async (): Promise<UserStatistics[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Bạn cần đăng nhập để xem thông tin');
    }

    const response = await axios.get(
      getApiUrl('/api/users'),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log("API getAllUsers response:", response.data);

    if (response.data && response.data.result === 'SUCCESS' && response.data.data) {
      // Chuyển đổi từ cấu trúc API sang cấu trúc UserStatistics
      return response.data.data.map((user: any) => ({
        userId: user.id,
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        avatarUrl: user.profileImageUrl || null,
        department: null,
        totalDistance: 0,
        totalMovingTime: null,
        totalElapsedTime: null,
        activityCount: 0,
        averagePace: null,
        averageSpeed: null,
        totalElevationGain: null,
        lastActivityDate: null
      }));
    }
    
    // Nếu cấu trúc response không khớp mẫu, thử tìm trong các thuộc tính khác
    if (response.data && Array.isArray(response.data)) {
      return response.data.map((user: any) => ({
        userId: user.id,
        fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        avatarUrl: user.profileImageUrl || user.avatarUrl || null,
        department: null,
        totalDistance: 0,
        totalMovingTime: null,
        totalElapsedTime: null,
        activityCount: 0,
        averagePace: null,
        averageSpeed: null,
        totalElevationGain: null,
        lastActivityDate: null
      }));
    }
    
    throw new Error('Không thể lấy danh sách người dùng');
  } catch (error: any) {
    console.error('Lỗi khi lấy danh sách người dùng:', error);
    if (error.response) {
      console.error('Chi tiết response:', error.response.status, error.response.data);
    }
    throw new Error(error.response?.data?.message || 'Không thể lấy danh sách người dùng');
  }
}; 